// Bluetooth printer utility for Capacitor (Android)
// With persistence and permission integration

import { savePrinterSelection, clearStoredPrinter } from '@/utils/printerStorage';
import { ensureBluetoothPermissions } from '@/utils/printerPermission';

declare global {
  interface Window {
    bluetoothSerial?: {
      list: (success: (devices: any[]) => void, error: (err: any) => void) => void;
      discoverUnpaired: (success: (devices: any[]) => void, error: (err: any) => void) => void;
      connect: (address: string, success: () => void, error: (err: any) => void) => void;
      disconnect: (success: () => void, error: (err: any) => void) => void;
      write: (data: string, success: () => void, error: (err: any) => void) => void;
      isConnected: (success: () => void, error: () => void) => void;
      isEnabled: (success: () => void, error: () => void) => void;
      enable: (success: () => void, error: (err: any) => void) => void;
      subscribe: (delimiter: string, success: (data: string) => void, error: (err: any) => void) => void;
      unsubscribe: (success: () => void, error: (err: any) => void) => void;
      clear: (success: () => void, error: (err: any) => void) => void;
    };
  }
}

// Enhanced function to list both paired and discoverable Bluetooth devices
// Requests permissions before scanning on Android
export async function listBluetoothDevices(): Promise<any[]> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    throw new Error('Bluetooth not supported on this platform.');
  }

  // Request permissions before scanning (Android 6+)
  await ensureBluetoothPermissions();

  // First check if Bluetooth is enabled
  const isEnabled = await checkBluetoothEnabled();
  if (!isEnabled) {
    throw new Error('Bluetooth is not enabled. Please enable Bluetooth and try again.');
  }

  try {
    // Get paired devices
    const pairedDevices = await getPairedDevices();
    console.log('Paired Bluetooth devices:', pairedDevices);
    
    // For better user experience, also try to discover unpaired devices
    // This is optional and may fail on some devices
    let discoveredDevices: any[] = [];
    try {
      discoveredDevices = await discoverUnpairedDevices();
      console.log('Discovered Bluetooth devices:', discoveredDevices);
    } catch (err) {
      console.warn('Failed to discover unpaired devices:', err);
      // Don't throw error here, just continue with paired devices
    }

    // Combine and deduplicate devices
    const allDevices = [...pairedDevices];
    discoveredDevices.forEach(device => {
      const exists = pairedDevices.find(paired => paired.address === device.address);
      if (!exists) {
        allDevices.push({ ...device, isPaired: false });
      }
    });

    // Mark paired devices
    allDevices.forEach(device => {
      if (!device.hasOwnProperty('isPaired')) {
        device.isPaired = true;
      }
    });

    return allDevices;
  } catch (error) {
    console.error('Error listing Bluetooth devices:', error);
    throw new Error(`Failed to scan for Bluetooth devices: ${error.message || error}`);
  }
}

// Get paired Bluetooth devices
async function getPairedDevices(): Promise<any[]> {
  return new Promise((resolve, reject) => {
    window.bluetoothSerial!.list(
      (devices: any[]) => {
        console.log('Successfully retrieved paired devices:', devices);
        resolve(devices || []);
      },
      (err: any) => {
        console.error('Error getting paired devices:', err);
        reject(new Error(`Failed to get paired devices: ${err.message || err}`));
      }
    );
  });
}

// Discover unpaired Bluetooth devices (optional)
async function discoverUnpairedDevices(): Promise<any[]> {
  return new Promise((resolve, reject) => {
    if (!window.bluetoothSerial!.discoverUnpaired) {
      resolve([]); // Not supported on this version
      return;
    }

    window.bluetoothSerial!.discoverUnpaired(
      (devices: any[]) => {
        console.log('Successfully discovered unpaired devices:', devices);
        resolve(devices || []);
      },
      (err: any) => {
        console.warn('Error discovering unpaired devices:', err);
        resolve([]); // Don't reject, just return empty array
      }
    );
  });
}

// Check if Bluetooth is enabled
export async function checkBluetoothEnabled(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    return false;
  }

  return new Promise((resolve) => {
    window.bluetoothSerial!.isEnabled(
      () => resolve(true),
      () => resolve(false)
    );
  });
}

// Enable Bluetooth (will prompt user)
export async function enableBluetooth(): Promise<void> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    throw new Error('Bluetooth not supported on this platform.');
  }

  return new Promise((resolve, reject) => {
    window.bluetoothSerial!.enable(
      () => resolve(),
      (err: any) => reject(new Error(`Failed to enable Bluetooth: ${err.message || err}`))
    );
  });
}

export async function connectBluetoothDevice(address: string, deviceName?: string): Promise<boolean> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    throw new Error('Bluetooth not supported on this platform.');
  }

  if (!address || address.trim() === '') {
    throw new Error('Device address is required.');
  }

  await ensureBluetoothPermissions();

  // Check if Bluetooth is enabled first
  const isEnabled = await checkBluetoothEnabled();
  if (!isEnabled) {
    throw new Error('Bluetooth is not enabled. Please enable Bluetooth and try again.');
  }

  return new Promise((resolve, reject) => {
    window.bluetoothSerial!.connect(
      address.trim(),
      () => {
        console.log(`Successfully connected to Bluetooth device: ${address}`);
        savePrinterSelection({ address: address.trim(), name: deviceName });
        resolve(true);
      },
      (err: any) => {
        console.error(`Failed to connect to Bluetooth device ${address}:`, err);
        reject(new Error(`Failed to connect to printer: ${err.message || err}`));
      }
    );
  });
}

export async function disconnectBluetoothDevice(): Promise<void> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    throw new Error('Bluetooth not supported on this platform.');
  }

  return new Promise((resolve, reject) => {
    window.bluetoothSerial!.disconnect(
      () => {
        console.log('Successfully disconnected from Bluetooth device');
        clearStoredPrinter();
        resolve();
      },
      (err: any) => {
        console.error('Failed to disconnect from Bluetooth device:', err);
        clearStoredPrinter(); // Clear on error too
        reject(new Error(`Failed to disconnect: ${err.message || err}`));
      }
    );
  });
}

const BLUETOOTH_CHUNK_SIZE = 512; // Safe chunk size for thermal printer MTU

export async function sendBluetoothData(data: string | Uint8Array): Promise<boolean> {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    throw new Error('Bluetooth not supported on this platform.');
  }

  if (!data || (typeof data === 'string' ? data.length === 0 : data.byteLength === 0)) {
    throw new Error('Data is required to send.');
  }

  const writeChunk = (chunk: string | Uint8Array): Promise<void> =>
    new Promise((resolve, reject) => {
      window.bluetoothSerial!.write(
        chunk as any,
        () => resolve(),
        (err: any) => reject(new Error(`Failed to send data: ${err.message || err}`))
      );
    });

  if (data instanceof Uint8Array && data.byteLength > BLUETOOTH_CHUNK_SIZE) {
    // Chunk binary data to avoid buffer overflow on thermal printers
    for (let i = 0; i < data.byteLength; i += BLUETOOTH_CHUNK_SIZE) {
      const chunk = data.subarray(i, Math.min(i + BLUETOOTH_CHUNK_SIZE, data.byteLength));
      await writeChunk(chunk);
      if (i + BLUETOOTH_CHUNK_SIZE < data.byteLength) {
        await new Promise((r) => setTimeout(r, 20)); // Small delay between chunks
      }
    }
    console.log('Successfully sent chunked binary data to Bluetooth device');
    return true;
  }

  return new Promise((resolve, reject) => {
    window.bluetoothSerial!.write(
      data as any,
      () => {
        console.log('Successfully sent data to Bluetooth device');
        resolve(true);
      },
      (err: any) => {
        console.error('Failed to send data to Bluetooth device:', err);
        reject(new Error(`Failed to send data: ${err.message || err}`));
      }
    );
  });
}

// Check if Bluetooth printer is connected (Android only)
export async function checkBluetoothPrinter() {
  if (typeof window === 'undefined' || !window.bluetoothSerial) {
    return { connected: false, error: 'Bluetooth not supported on this platform.' };
  }

  return new Promise((resolve) => {
    window.bluetoothSerial!.isConnected(
      () => resolve({ connected: true }),
      () => resolve({ connected: false, error: 'Bluetooth printer not connected.' })
    );
  });
}

// Utility function to format device name for display
export function formatDeviceName(device: any): string {
  if (!device) return 'Unknown Device';
  
  let name = device.name || device.address || 'Unknown Device';
  
  // Add connection status indicator
  if (device.isPaired === false) {
    name += ' (Discoverable)';
  } else if (device.isPaired !== false) {
    name += ' (Paired)';
  }
  
  return name;
}

// Check if device appears to be a printer based on name
export function isPrinterDevice(device: any): boolean {
  if (!device || !device.name) return false;
  
  const name = device.name.toLowerCase();
  const printerKeywords = ['printer', 'print', 'pos', 'receipt', 'thermal', 'bluetooth printer'];
  
  return printerKeywords.some(keyword => name.includes(keyword));
} 